import { NetworkStatus } from 'apollo-client'

import {
  GetTrashQuery,
  GetTrashQueryVariables,
  useGetTrashQuery,
} from 'gqlTypes'

import { handleFetchMore } from 'components/InfiniteList'
import { ErrorHandler } from 'modules/tracing'
import { useOnEvent } from 'utils/eventEmitter'

export interface GetTrashProps {
  workspaceId: string
  search?: string
  skip?: boolean
}

const trashItemsPath = ['workspaceTrash', 'entries']

export const useGetTrash = (props: GetTrashProps) => {
  const { workspaceId, search = '' } = props
  const {
    loading,
    data,
    error,
    fetchMore,
    refetch,
    networkStatus,
  } = useGetTrashQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      identifier: workspaceId,
      after: null,
      search: {
        name: search,
      },
    },
  })

  useOnEvent('emptiedWorkspaceTrash', ({ workspaceIdentifier }) => {
    if (workspaceIdentifier !== workspaceId) {
      return
    }
    refetch()
  })

  if (
    (loading && !data?.workspaceTrash) ||
    // right now the only place when we use refetch is to fetch
    // new data after trash was emptied and for this scenario
    // when refetch is happening we want to show a spinner instead
    // of the "no documents" message.

    // This is needed as any team member can empty the trash,
    // however, drafts documents that are in your trash won't be deleted (API)
    // hence we need to refetch the current page to get the accurate results

    // if we'll ever want to use refetch for any other purpose, we might
    // need to rethink when we show the `loading` page.
    networkStatus === NetworkStatus.refetch
  ) {
    return { kind: 'loading' } as const
  }

  if (error) {
    return { kind: 'error', reason: error.message } as const
  }
  if (!data) {
    const reason = 'data should always be truthy in final state' as string
    ErrorHandler.shouldNeverHappen(reason)
    return { kind: 'error', reason } as const
  }

  const { entries, meta } = data.workspaceTrash
  const { totalCount, after } = meta

  return {
    kind: 'data',
    items: entries,
    loading,
    totalCount,
    fetchMore: handleFetchMore<GetTrashQuery, GetTrashQueryVariables>(
      fetchMore,
      trashItemsPath,
      { after }
    ),
  } as const
}
